import { useCallback, useMemo, useState } from "react";
import { propertiesService } from "@/services/propertiesService";
import { usePagedResource } from "@/hooks/usePagedResource";
import { usePageScope, useSettingsStore } from "@/store/settingsStore";
import { useCrmMappingSyncStore } from "@/store/crmMappingSyncStore";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import { scopeParams } from "@/lib/scopeParams";
import type { PropertyFilter, PropertySummary } from "@/types/entities";
import type { PageRequest, PageResponse } from "@/types/api";

export function usePropertiesList(initialFilter?: PropertyFilter, initialPage?: PageRequest) {
  const { legalEntityId, branchId } = useSettingsStore();
  const scope = usePageScope("properties");
  const crmMappingEpoch = useCrmMappingSyncStore((s) => s.crmMappingEpoch);
  // The picker's selection wins where it has one; the org context and any pinned branch
  // remain the fallback, which is what an empty selection ("everything I can see") means.
  const scopeQuery = useMemo(() => scopeParams(scope), [scope]);

  const fetcher = useCallback(
    (filter: PropertyFilter, page: PageRequest): Promise<PageResponse<PropertySummary>> =>
      propertiesService.list(
        { ...filter, ...contactPropertyListScope(legalEntityId, branchId), ...scopeQuery },
        page,
      ),
    [legalEntityId, branchId, scopeQuery, crmMappingEpoch],
  );

  return usePagedResource<PropertySummary, PropertyFilter>(fetcher, {
    initialPage: initialPage ?? { page: 0, size: 30, sort: "atlasModifiedAt", direction: "desc" },
    initialFilter,
  });
}

/**
 * Multi-select state for the properties list.
 *
 * Keyed by id and kept outside the row array, so a selection survives paging,
 * sorting and filtering — setting availability across a dozen listings usually
 * means picking a few, moving to the next page, and picking a few more. The row
 * itself is remembered (not just its id) because the bulk actions need each
 * property's `sourceCrm` / `externalId` refs long after its page was replaced.
 */
export function usePropertySelection() {
  const [selected, setSelected] = useState<Record<string, PropertySummary>>({});

  const selectedIds = useMemo(() => Object.keys(selected), [selected]);
  const selectedKeys = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedRows = useMemo(() => Object.values(selected), [selected]);

  const toggle = useCallback((row: PropertySummary, next: boolean) => {
    setSelected((current) => {
      const updated = { ...current };
      if (next) updated[row.id] = row;
      else delete updated[row.id];
      return updated;
    });
  }, []);

  const toggleAll = useCallback((rows: PropertySummary[], next: boolean) => {
    setSelected((current) => {
      const updated = { ...current };
      for (const row of rows) {
        if (next) updated[row.id] = row;
        else delete updated[row.id];
      }
      return updated;
    });
  }, []);

  const clear = useCallback(() => setSelected({}), []);

  return { selectedIds, selectedKeys, selectedRows, toggle, toggleAll, clear };
}
